<template>
  <b-modal
    id="preview-page"
    ref="preview-page"
    centered
    hide-footer
    header-class="p-0 d-flex align-items-center justify-content-between"
    ok-only
    no-close-on-backdrop
    :hide-header="inline"
    :hide-backdrop="inline"
    :no-enforce-focus="inline"
    :modal-class="inline ? 'wm-preview-inline' : ''"
    @hide="closePreviewModal"
  >
    <!-- v3 editor (founder 17./18.10.2026): the preview takes the canvas' place under the header bar, which keeps
         Edit | Preview and the device icons; the phone and tablet mockups stay. -->
    <div id="ams-preview" :class="[previewDeviceClass, { 'with-comments': !!comments && panelOpen, bare }]">
      <div id="ams-preview-stage">
        <div id="ams-preview-content">
          <iframe ref="previewFrame" width="100%" height="100%" :src="iframeSrcWithToken"></iframe>
        </div>
        <div id="ams-preview-phone" class="ams-preview-device"></div>
        <div id="ams-preview-tablet" class="ams-preview-device"></div>
        <div id="ams-preview-fullscreen" class="ams-preview-device"></div>
      </div>
      <!-- Inline there is no browse | comment switch: Comments always comments, Preview browses (founder 20.10.2026). -->
      <button
        v-if="comments && !panelOpen && (!inline || commentsView)" type="button" class="comments-tab-toggle" data-test="comments-tab" @click="panelOpen = true"
      >{{ $t('webmag.comments.showPanel', { n: openCount }) }}</button>
    </div>
    <!-- Comments plan 2: comment on what the preview shows; the reader page reports places, this panel writes. -->
    <preview-comments
      v-if="comments"
      v-show="panelOpen"
      v-bind="comments"
      :pending-anchor="pendingAnchor"
      :device="device"
      :focus-request="focusRequest"
      @pins="sendPins"
      @focus-pin="(id) => postToFrame('focus', { id })"
      :capture="captureSeen"
      :start-focus="start && start.focus ? start.focus : null"
      @anchor-used="pendingAnchor = null"
      @applied="refreshAfterApply"
      @hidden="panelOpen = false"
      @count="(n) => { openCount = n; }"
    />
    <template #modal-header="{ ok }">
      <div class="d-flex align-items-center">
        <div style="background-color: #53575b;">
          <img
            class="m-3"
            src="@/assets/logo.png"
            alt="Webmag Logo"
            style="width: 33px;"
          />
        </div>
        <b-breadcrumb class="mb-0 ml-4">
          <b-breadcrumb-item @click="closeModalOfBreadcrumbClick">
            <h2
              id="tooltip-button-variant-item-title"
              class="mb-0"
            >
              {{ itemName | sliceStingPos30 }}
            </h2>
            <b-tooltip
              v-if="itemName.length > 30"
              target="tooltip-button-variant-item-title"
            >
              {{ itemName }}
            </b-tooltip>
          </b-breadcrumb-item>
          <b-breadcrumb-item
            active
          >
            <h2 class="mb-0">{{ $t('webmag.preview') }}</h2>
          </b-breadcrumb-item>
        </b-breadcrumb>
      </div>
      <div v-if="comments" class="preview-header-switch">
        <preview-mode-bar :comment-mode.sync="commentMode" />
      </div>
      <div v-if="pzClients.length > 0" class="d-flex ml-auto mr-2">
        <b-form-select
          id="pz-clients"
          v-model="selectedClient"
          :options="pzClients"
          @input="changeSelectedClient"
        ></b-form-select>
      </div>
      <div class="d-flex">
        <div>
          <b-button
            id="phone-button"
            @click="changeDevice('phone')"
            class="dark-grey-buttons"
          >
            <b-icon
              icon="phone"
              aria-hidden="true"
              class="top--2"
            ></b-icon>
          </b-button>
          <b-button
            id="tablet-button"
            @click="changeDevice('tablet')"
            class="dark-grey-buttons ml-2"
          >
            <b-icon
              icon="tablet"
              aria-hidden="true"
              class="top--2"
            ></b-icon>
          </b-button>
          <b-button
            id="fullscreen-button"
            @click="changeDevice('fullscreen')"
            class="dark-grey-buttons ml-2 active"
          >
            <b-icon
              icon="laptop"
              aria-hidden="true"
              class="top--2"
            ></b-icon>
          </b-button>
        </div>

        <b-button
          @click="ok()"
          class="mx-4 dark-grey-buttons"
        >
          <b-icon
            icon="arrow-left-circle-fill"
            aria-hidden="true"
            class="top--2"
          ></b-icon>
        </b-button>
      </div>
    </template>
  </b-modal>
</template>

<script>
import GetPersonalizedVariableSetsItems from '@/graphQlQueries/queries/getPersonalizedVariableSetsItems';
import SetPreviewWindowInCache from '@/graphQlQueries/mutations/setPreviewWindowInCache';
import SendPublishedDataToServer from '@/mixins/sendPublishedDataToServer';
import DeleteCacheFetchMagazine from '@/graphQlQueries/mutations/deleteCacheFetchMagazine';
import SetFolderTailwindCss from '@/graphQlQueries/mutations/setFolderTailwindCss';
import SetProjectTailwindCss from '@/graphQlQueries/mutations/setProjectTailwindCss';
import GetPageParentIds from '@/graphQlQueries/queries/getPageParentIds';
import { buildMagazineTailwind } from '@/utils/buildTailwindForMagazine';
import { collectPageV2Css } from '@/utils/buildPageV2Css';
import SetPageV2CssDraft from '@/graphQlQueries/mutations/setPageV2CssDraft';
import LeserVorschauPass from '@/graphQlQueries/mutations/leserVorschauPass';
import PreviewPass from '@/graphQlQueries/mutations/previewPass';
import PreviewComments from '@/components/comments/PreviewComments.vue';
import PreviewModeBar from '@/components/comments/PreviewModeBar.vue';
import { acceptMessage, deviceOf } from '@/utils/commentBridge';
import { clientSetup } from '@/utils/clientSetup';

/** The mockup for a header tier: phone, tablet, or the full width for desktop and PDF. */
export function deviceForTier(tier) {
  if (tier === 'base' || tier === 'sm') return 'phone';
  if (tier === 'md' || tier === 'lg') return 'tablet';
  return 'fullscreen';
}

export default {
  name: 'ModalPreviewPage',
  components: { PreviewComments, PreviewModeBar },
  mixins: [SendPublishedDataToServer],
  props: {
    iframeSrc: {
      type: String,
      required: true,
    },
    previewActionObject: {
      type: Object,
      required: false,
    },
    itemName: {
      type: String,
      required: true,
    },
    groupDomain: {
      type: String,
      required: true,
    },
    itemPath: {
      type: String,
      required: false,
    },
    // Comments plan 2: props for PreviewComments when comments are on (reader v3 only), else null.
    comments: {
      type: Object,
      default: null,
    },
    // Writes the draft translation of the shown language before the reader loads it (plan 2).
    prepareLanguage: {
      type: Function,
      default: null,
    },
    // How the preview starts: { comments: true } from the comment button (Rev. 3), null = as usual.
    start: {
      type: Object,
      default: null,
    },
  },
  data: () => ({
    personalizedItemSetsByItemPath: null,
    deviceName: 'fullscreen',
    iframeSrcWithToken: null,
    containerDOM: null,
    contentDOM: null,
    changeInterval: null,
    stageDOM: false,
    isFullscreen: false,
    isPhone: false,
    isTablet: false,
    stage: {},
    previewPass: null,
    transform: null,
    selectedClient: null,
    pzClients: [],
    runGetSetsByPath: true,
    // A v3 editor shows the preview in place of the canvas, at the device the header bar picked.
    inline: false,
    tier: 'xl',
    commentMode: false,
    // Inline: the header's Comments is open (bare page and panel); browse | comment inside it is commentMode.
    commentsView: false,
    // Comments Rev. 3: the panel can be hidden; a tab with the open count brings it back.
    panelOpen: false,
    openCount: 0,
    pendingAnchor: null,
    pendingSpot: null,
    focusRequest: null,
    pins: [],
  }),
  created() {
    // Opened from the comment button: comment mode and the panel at once.
    if (this.start && this.start.comments) {
      this.commentMode = true;
      this.panelOpen = true;
      this.commentsView = true;
    }
    this.$root.$on('keditor-preview-mode', this.followEditor);
    this.$root.$on('keditor-mobile-preview', this.followTier);
    this.$root.$on('keditor-preview-close', this.closeInline);
    this.$root.$on('preview-set-comment-mode', this.setCommentMode);
    // KEditor answers right away, so the first render already knows.
    this.$root.$emit('keditor-preview-frage');
  },
  beforeDestroy() {
    this.$root.$off('keditor-preview-mode', this.followEditor);
    this.$root.$off('keditor-mobile-preview', this.followTier);
    this.$root.$off('keditor-preview-close', this.closeInline);
    this.$root.$off('preview-set-comment-mode', this.setCommentMode);
    this.$root.$emit('preview-comment-mode', false);
    window.removeEventListener('message', this.onFrameMessage);
  },
  async mounted() {
    // Rebuild the magazine's Tailwind CSS from the current page's DRAFT HTML
    // BEFORE deleteCache(), so the preview iframe's fetchPreview call returns
    // freshly-compiled CSS that covers any new tw: classes added since the
    // last publish. No-op unless the caller (PageItem.vue) supplied pageId +
    // currentPageHtml on previewActionObject.
    //
    // Draft-to-production leakage note: this write temporarily overwrites
    // the magazine's tailwind_css with a superset that includes unpublished
    // classes. The next publish rebuilds from published-only manifests, so
    // the extras drop out on their own. Acceptable by design.
    try {
      const pao = this.previewActionObject || {};
      if (pao.pageId != null && pao.currentPageHtml) {
        const parentIdsResult = await this.$apollo.query({
          query: GetPageParentIds,
          variables: { pageId: pao.pageId },
          fetchPolicy: 'network-only',
        });
        const row = parentIdsResult?.data?.pages_by_pk;
        const parentFolderId = row?.parent_folder_id ?? null;
        const parentProjectId = row?.parent_project_id ?? null;
        if (parentFolderId != null || parentProjectId != null) {
          const tailwindCss = await buildMagazineTailwind({
            apollo: this.$apollo,
            parentFolderId,
            parentProjectId,
            pageId: pao.pageId,
            pageHtml: pao.currentPageHtml,
          });
          await this.$apollo.mutate({
            mutation: parentProjectId ? SetProjectTailwindCss : SetFolderTailwindCss,
            variables: parentProjectId
              ? { projectId: parentProjectId, tailwindCss }
              : { folderId: parentFolderId, tailwindCss },
          });
        }
        // Keep the DRAFT v2 CSS fresh from the current draft html — same
        // "compute here, in the same try/catch as the tailwind rebuild"
        // pattern (Task 7). Unlike PageItem.vue/Dashboard.vue there is no
        // publishedData.php POST in this flow to also attach the value to —
        // this preview iframe renders live (files/_app_dev/js/lib.file.js's
        // loadCustomCSS()/applyPageV2Css()), not from a PHP-generated file,
        // and that client-side reader gets the value straight off the SAME
        // `pages` row this mutation writes — there is no OTHER channel to
        // reach it through.
        //
        // DRAFT/PUBLISHED SPLIT (runtime-css task): this used to write
        // `pages.v2_css`, the very column the PUBLISH path writes the
        // PUBLISHED CSS to, which was only ever safe while the runtime
        // reader was gated to preview loads. The runtime reader now also
        // runs for LIVE visitors of a published magazine (the published SPA
        // fetches its pages at runtime through fetchMagazine — the static
        // `<slug>.html` with its baked `<style id="wm-v2">` is not the
        // document the product URL resolves to), so a draft in `v2_css`
        // would be a real leak. Therefore this writes `pages.v2_css_draft`
        // ONLY:
        //   * `pages.v2_css`       — publish-only truth, exposed by
        //                            fetch_magazine's PUBLISHED branch,
        //                            read by lib.file.js on live loads.
        //   * `pages.v2_css_draft` — preview-only, exposed by
        //                            fetch_magazine's PREVIEW branch
        //                            (_mag_joined_preview_pages) and NOT by
        //                            the published one, read by lib.file.js
        //                            only when `p$.settings.preview`.
        // The tailwind_css write above still shares one column between draft
        // and published (magazine-scoped, self-heals on the next publish of
        // any page) — that trade-off is unchanged and out of scope here.
        await this.$apollo.mutate({
          mutation: SetPageV2CssDraft,
          variables: { pageId: pao.pageId, v2CssDraft: collectPageV2Css(pao.currentPageHtml) },
        });
      }
    } catch (tailwindErr) {
      console.error('[ModalPreviewPage] Tailwind preview rebuild failed — preview may show stale CSS:', tailwindErr);
    }

    if (this.leser) {
      if (this.comments) window.addEventListener('message', this.onFrameMessage);
      await this.loadLeser();
    } else {
      await this.deleteCache();
      // A signed one-hour pass instead of the login token: the token in ?token= ended up in the reader's
      // server logs and history (security fix 17.10.2026). Without a pass (no access, e.g. a template of
      // another account) the published version is shown.
      this.previewPass = await this.fetchPreviewPass();
      this.iframeSrcWithToken = this.previewUrl('');
    }
    this.transform = (() => {
      if (document.documentElement.style.transform == null) {
        const vendors = ['Webkit', 'Moz', 'ms'];
        for (let i = 0; i < vendors.length; i++) {
          if (document.documentElement.style[`${vendors[i]}Transform`] !== undefined) {
            return `${vendors[i]}Transform`;
          }
        }
      }
      return 'transform';
    });
    this.$nextTick(() => {
      window.addEventListener('resize', () => {
        window.requestAnimationFrame(this.previewResize);
      }, false);
      window.requestAnimationFrame(this.previewResize);
    });
    this.$bvModal.show('preview-page');
    if (this.inline && deviceForTier(this.tier) !== 'fullscreen') {
      this.$nextTick(() => this.changeDevice(deviceForTier(this.tier)));
    }
    if (!this.leser) await this.sendPreviewActionToServer('openPreview');
  },
  watch: {
    commentMode: {
      immediate: true,
      handler(on, was) {
        if (was !== undefined) {
          this.postToFrame('mode', { on });
          this.postToFrame('pins', { pins: this.shownPins });
        }
        if (on) this.panelOpen = true;
      },
    },
    // The header bar lights Comments or Preview.
    commentsView: {
      immediate: true,
      handler(on) {
        this.$root.$emit('preview-comment-mode', on);
        this.postToFrame('pins', { pins: this.shownPins });
      },
    },
    // Another language picked in the header while the preview is open: the reader loads it (founder 19.10.2026).
    iframeSrc(next, was) {
      if (this.leser && next !== was) this.loadLeser();
    },
    // Inline, Comments and Preview switch between the bare page and the mockups: the stage scales again.
    // Comments ↔ Preview: _previewModal.scss delays size and transform by 0.8 s (made for the device fade), so the
    // page showed at the bare place first and then jumped into the mockup. Without transitions it lands at once.
    bare() {
      const content = document.getElementById('ams-preview-content');
      if (content) {
        content.style.transition = 'none';
        content.style.opacity = 0;
      }
      this.$nextTick(() => {
        this.previewResize(true);
        window.requestAnimationFrame(() => window.requestAnimationFrame(() => {
          if (!content) return;
          content.style.removeProperty('transition');
          content.style.opacity = 1;
        }));
      });
    },
  },
  computed: {
    // Comments Rev. 3: the preview for comments has no device mockups; inline that follows the header's Comments.
    bare() {
      if (this.inline) return this.commentsView;
      return !!(this.start && this.start.comments);
    },
    // The header bar's Preview is the page without comments (founder 19.10.2026); Comments shows them.
    shownPins() {
      return this.inline && !this.commentsView ? [] : this.pins;
    },
    device() {
      return deviceOf(this.deviceName);
    },
    leser() { return (this.previewActionObject && this.previewActionObject.leser) || null; },
    previewDeviceClass() {
      return `preview-${this.deviceName}`;
    },
  },
  apollo: {
    personalizedItemSetsByItemPath: {
      query: GetPersonalizedVariableSetsItems,
      variables() {
        return {
          itemPath: this.itemPath,
          addValues: true,
        };
      },
      update(data) {
        // make sure that the personalized clients are only fetched once
        this.runGetSetsByPath = false;

        data.getPersonalizedVariableSetsByPath.forEach((set) => {
          if (set.values && set.values.length) {
            set.values.forEach((item) => {
              if (!this.pzClients.some((client) => client.value === item.client_key)) {
                this.pzClients.push({
                  value: item.client_key,
                  text: item.name,
                });
              }
            });
          }
        });
        if (this.pzClients.length > 0) {
          this.pzClients.unshift({
            value: null,
            text: this.$t('preview.noItemSelected'),
          });
        }
      },
      skip() {
        return !this.itemPath || this.runGetSetsByPath === false;
      },
      fetchPolicy: 'network-only',
    },
  },
  filters: {
    sliceStingPos30(value) {
      return (value.length > 30) ? `${value.slice(0, 30)}...` : value;
    },
  },
  methods: {
    followEditor(mode, v3) {
      this.inline = v3 === true;
      this.followTier(mode);
    },
    followTier(mode) {
      if (!mode) return;
      this.tier = mode;
      // Only once the stage is rendered; mounted() applies the first device.
      if (this.inline && document.getElementById('ams-preview-content') && deviceForTier(mode) !== this.deviceName) this.changeDevice(deviceForTier(mode));
    },
    /** The header bar's Comments | Preview while the inline preview is open. */
    setCommentMode(on) {
      this.commentsView = on === true;
      this.commentMode = this.commentsView;
      if (this.inline) this.panelOpen = this.commentsView;
    },
    closeInline() {
      this.$bvModal.hide('preview-page');
    },
    postToFrame(type, payload) {
      const frame = this.$refs.previewFrame;
      if (!frame || !frame.contentWindow || !this.iframeSrcWithToken) return;
      try {
        frame.contentWindow.postMessage({ source: 'wm-comments', type, payload }, new URL(this.iframeSrcWithToken).origin);
      } catch (e) {
        // No valid reader address: nothing to talk to.
      }
    },
    /** Comments plan 3: an applied change request shows once saved (the draft translation first for a language). */
    refreshAfterApply() {
      let fired = false;
      const reload = async () => {
        if (fired) return;
        fired = true;
        this.$root.$off('keditor-saved', reload);
        if (this.prepareLanguage) await this.prepareLanguage();
        if (this.iframeSrcWithToken) this.iframeSrcWithToken = this.iframeSrcWithToken.replace(/(&r=\d+)?$/, `&r=${Date.now()}`);
      };
      this.$root.$on('keditor-saved', reload);
      setTimeout(reload, 6000);
    },
    /** Reader v3 (spec 3b Rev. 3 §4): a one-hour ed. pass in ?preview=; no token, no old reader cache, no PHP preview files. */
    async loadLeser() {
      if (this.prepareLanguage) await this.prepareLanguage();
      try {
        const { data } = await this.$apollo.mutate({ mutation: LeserVorschauPass, variables: { projectId: this.leser.projectId } });
        this.iframeSrcWithToken = `${this.iframeSrc}?preview=${encodeURIComponent(data.leserVorschauPass.pass)}`;
      } catch (e) {
        console.error('[ModalPreviewPage] reader v3 preview pass failed', e);
        this.iframeSrcWithToken = this.iframeSrc;
      }
    },
    sendPins(pins) {
      this.pins = pins;
      this.postToFrame('pins', { pins: this.shownPins });
    },
    /**
     * Plan 3c (spec Rev. 4): the writer's setup and what the server screenshot should show: the picked spot's view and
     * scroll, else the frame's size at the top (a page comment).
     */
    captureSeen() {
      const frame = this.$refs.previewFrame;
      const spot = this.pendingAnchor && this.pendingSpot;
      const view = (spot && spot.view) || (frame ? { w: frame.clientWidth, h: frame.clientHeight } : null);
      const dpr = window.devicePixelRatio || 1;
      return {
        setup: clientSetup(window.navigator, window.screen, view, dpr),
        shot: view && this.iframeSrcWithToken
          ? {
            url: this.iframeSrcWithToken, width: view.w, height: view.h, dpr, scrollY: (spot && spot.scrollY) || 0,
          }
          : null,
      };
    },
    onFrameMessage(event) {
      const frame = this.$refs.previewFrame;
      const m = acceptMessage(event, frame && frame.contentWindow, this.iframeSrcWithToken);
      if (!m) return;
      if (m.type === 'ready') {
        this.postToFrame('mode', { on: this.commentMode });
        this.postToFrame('pins', { pins: this.shownPins });
        // Plan 4: a thread opened by link is focused on the page too, once the page can hear it.
        if (this.start && this.start.focus) this.postToFrame('focus', { id: this.start.focus });
      } else if (m.type === 'place') {
        this.pendingAnchor = (m.payload && m.payload.anchor) || null;
        // Plan 3c: what the writer saw there, for the server screenshot.
        this.pendingSpot = m.payload ? { view: m.payload.view, scrollY: m.payload.scrollY } : null;
      } else if (m.type === 'open' && m.payload) {
        this.focusRequest = { id: m.payload.id, n: Date.now() };
      }
    },
    getSlugPath() {
      let slugPath = null;
      if (this.previewActionObject && this.previewActionObject.parentSlugPath) {
        slugPath = this.previewActionObject.parentSlugPath;
      } else if (this.itemPath) {
        slugPath = this.itemPath;
      }
      if (!slugPath) {
        return null;
      }
      return slugPath.startsWith('/') ? slugPath.slice(1) : slugPath;
    },
    deleteCache() {
      return new Promise((resolve, reject) => {
        const slug_path = this.getSlugPath();
        if (!this.groupDomain || !slug_path) {
          resolve();
          return;
        }
        const domain = `${this.groupDomain}/`;
        try {
          this.$apollo.mutate({
            mutation: DeleteCacheFetchMagazine,
            variables: {
              domain,
              slug_path,
            },
          }).then((response) => {
            console.log('response', response);
            resolve();
          });
        } catch (error) {
          console.error(error);
          reject(error);
        }
      });
    },
    async fetchPreviewPass() {
      const target = this.previewActionObject && this.previewActionObject.previewTarget;
      if (!target) return null;
      try {
        const { data } = await this.$apollo.mutate({ mutation: PreviewPass, variables: target });
        return data.previewPass.pass;
      } catch (e) {
        console.error('[ModalPreviewPage] preview pass failed', e);
        return null;
      }
    },
    previewUrl(pz) {
      const pzParam = `pz=${pz === '' ? '""' : encodeURIComponent(pz)}`;
      return this.previewPass ? `${this.iframeSrc}?${pzParam}&preview=${encodeURIComponent(this.previewPass)}` : `${this.iframeSrc}?${pzParam}`;
    },
    async changeSelectedClient(selectedClient) {
      await this.deleteCache();
      this.iframeSrcWithToken = this.previewUrl(selectedClient);
    },
    previewResize(reset) {
      let scale = 1;
      if (!this.stageDOM || reset !== undefined) {
        this.stageDOM = document.getElementById('ams-preview-stage');
        // check if the dom is already rendered
        if (!this.stageDOM) {
          return false;
        }
        this.containerDOM = document.getElementById('ams-preview');
        const classes = this.containerDOM.classList;
        this.isFullscreen = classes.contains('preview-fullscreen');
        this.isPhone = classes.contains('preview-phone');
        this.isTablet = classes.contains('preview-tablet');
        // used automatic getPropertyValue("max-width") before - but safari problems
        if (this.isFullscreen) {
          this.stage.width = document.documentElement.clientWidth;
          this.stage.height = document.documentElement.clientHeight;
        } else if (this.isPhone) {
          this.stage.width = 720;
          this.stage.height = 680;
        } else if (this.isTablet) {
          this.stage.width = 850;
          this.stage.height = 750;
        }
      }
      scale = Math.min(
        document.documentElement.clientWidth / this.stage.width,
        document.documentElement.clientHeight / this.stage.height,
      );
      // Commenting shows the page as it is, without device frames and without scaling (Rev. 3).
      if (this.isFullscreen || scale > 1 || this.bare) {
        scale = 1;
      }
      this.stageDOM.style[this.transform] = `scale3D(${scale},${scale},${scale})`;
      return true;
    },
    setActiveClasses(deviceName) {
      // The inline preview has no header of its own: the bar shows the device.
      if (this.inline) return;
      const phoneButton = document.getElementById('phone-button').classList;
      const tabletButton = document.getElementById('tablet-button').classList;
      const fullScreenButton = document.getElementById('fullscreen-button').classList;

      phoneButton.remove('active');
      tabletButton.remove('active');
      fullScreenButton.remove('active');

      // set the active class
      switch (deviceName) {
        case 'phone':
          phoneButton.add('active');
          break;
        case 'tablet':
          tabletButton.add('active');
          break;
        case 'fullscreen':
          fullScreenButton.add('active');
          break;
        default:
          console.log('no matching device');
      }
    },
    changeDevice(deviceName) {
      this.setActiveClasses(deviceName);
      const devices = document.getElementById('ams-preview').classList;

      this.deviceName = deviceName;
      this.containerDOM = document.getElementById('ams-preview');
      this.contentDOM = document.getElementById('ams-preview-content');

      switch (deviceName) {
        case 'phone':
          this.addAndRemoveClassesOfPhone(devices);
          this.doTheAnimation();
          break;
        case 'tablet':
          this.addAndRemoveClassesOfTablet(devices);
          this.doTheAnimation();
          break;
        case 'fullscreen':
          this.addAndRemoveClassesOfDesktop(devices);
          break;
        default:
          console.error('wrong device name');
      }
    },
    addAndRemoveClassesOfPhone(devices) {
      if (
        devices.contains('preview-phone')
        && !devices.contains('preview-phone-landscape')
      ) {
        devices.add('preview-landscape');
        devices.add('preview-phone-landscape');
      } else {
        devices.remove('preview-landscape');
        devices.remove('preview-phone-landscape');
      }
      devices.remove('preview-tablet-landscape');
      devices.add('preview-phone');
      devices.remove('preview-tablet');
      devices.remove('preview-fullscreen');
      devices.remove('hide-preview-3d');
      devices.add('preview-device-switch');
    },
    addAndRemoveClassesOfTablet(devices) {
      if (devices.contains('preview-tablet') && !devices.contains('preview-tablet-landscape')) {
        devices.add('preview-landscape');
        devices.add('preview-tablet-landscape');
      } else {
        devices.remove('preview-landscape');
        devices.remove('preview-tablet-landscape');
      }

      devices.remove('preview-phone-landscape');
      devices.add('preview-tablet');
      devices.remove('preview-phone');
      devices.remove('preview-fullscreen');
      devices.remove('hide-preview-3d');
      devices.add('preview-device-switch');
    },
    addAndRemoveClassesOfDesktop(devices) {
      devices.remove('preview-phone-landscape');
      devices.remove('preview-tablet-landscape');
      devices.remove('preview-landscape');

      if (!devices.contains('preview-fullscreen')) {
        devices.add('preview-fullscreen');
        devices.remove('preview-phone');
        devices.remove('preview-tablet');
        devices.remove('preview-3d');
        devices.add('hide-preview-3d');

        devices.add('preview-device-switch');
        // we put the animation here because we don't have
        // landscape and portrait in desktop mode
        this.doTheAnimation();
      }
    },
    doTheAnimation() {
      // Bare preview (comments Rev. 3): only the width changes, no fade like a reload.
      if (this.bare) {
        this.containerDOM.classList.remove('preview-device-switch');
        return;
      }
      // do that for the animation
      setTimeout(() => {
        this.previewResize(true);
      }, 500);

      this.contentDOM.style.opacity = 0.001;
      window.clearInterval(this.changeInterval);
      this.changeInterval = setTimeout(() => {
        this.containerDOM.classList.remove('preview-device-switch');
        this.contentDOM.style.opacity = 1;
      }, 1100);
    },
    closeModalOfBreadcrumbClick() {
      this.$bvModal.hide('preview-page');
      this.closePreviewModal();
    },
    closePreviewModal() {
      document.body.className = document.body.className.replace('preview-modal-open', '');
      if (!this.leser) this.sendPreviewActionToServer('closePreview');
      this.$emit('preview-closed');

      this.$apollo.mutate({
        mutation: SetPreviewWindowInCache,
        variables: {
          isPreviewWindowOpen: false,
        },
      });
    },
    async sendPreviewActionToServer(previewAction) {
      try {
        await this.sendPublishedDataToServer({
          action: previewAction,
          data: this.previewActionObject,
        });
      } catch (ex) {
        console.log(ex);
      }
    },
  },
};
</script>

<style lang="scss">
@import '../../scss/_previewModal.scss';

// Comments plan 2: the comments panel (380px) docks on the right; the page keeps the rest.
#ams-preview.with-comments {
  right: 380px;
}

// Comments Rev. 4: the browse/comment switch in the middle of the header.
#preview-page .modal-header {
  position: relative;
}

.preview-header-switch {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}

// Comments Rev. 3: no device mockups; the page fills the space (phone and tablet just narrower, centred).
#ams-preview.bare {
  right: 0;
  background: #e5e7eb;

  &.with-comments {
    right: 380px;
  }

  .ams-preview-device {
    display: none !important;
  }

  #ams-preview-stage {
    position: absolute;
    inset: 0;
    width: auto !important;
    height: auto !important;
    transform: none !important;
  }

  #ams-preview-content {
    position: absolute !important;
    top: 0 !important;
    bottom: 0 !important;
    left: 0 !important;
    width: 100% !important;
    height: 100% !important;
    transform: none !important;
    border-radius: 0 !important;
    background: #fff;
    opacity: 1 !important;
    transition: none !important;
  }

  &.preview-phone #ams-preview-content {
    left: 50% !important;
    width: 390px !important;
    transform: translateX(-50%) !important;
    box-shadow: 0 0 0 1px #d1d5db;
  }

  &.preview-tablet #ams-preview-content {
    left: 50% !important;
    width: 808px !important;
    max-width: 100%;
    transform: translateX(-50%) !important;
    box-shadow: 0 0 0 1px #d1d5db;
  }
}

.comments-tab-toggle {
  position: absolute;
  top: 16px;
  right: 16px;
  z-index: 10;
  padding: 6px 12px;
  border: 0;
  border-radius: 16px;
  background: #1f2937;
  color: #fff;
  font-size: 13px;
  font-weight: 500;
  box-shadow: 0 4px 12px rgba(15, 23, 42, 0.18);
}

#preview-page___BV_modal_body_ {
  background-color: #44484c;
  background-image: radial-gradient(circle, #696c70 10%, #44484c 80%);
}

#preview-page___BV_modal_footer_ {
  justify-content: space-between;

  > div button {
    margin-right: 8px;
  }
}

// v3: under the header bar (fixed-top, z-index 1030) so its menus open above the preview.
// BootstrapVue puts the modal in a wrapper at z-index 1040, over the header bar (1030): its menus then opened under
// the preview. Inline the preview sits below the bar, so the wrapper drops under it too.
#preview-page___BV_modal_outer_:has(> .wm-preview-inline) {
  z-index: 1020 !important;
}

#preview-page.wm-preview-inline {
  top: var(--wm-header-height, 48px);
  // Bootstrap's .modal is 100% high: under the bar it ran 48 px past the screen and cut the desktop mockup.
  bottom: 0;
  height: auto;
  z-index: 1020;

  .modal-dialog,
  .modal-content {
    height: 100%;
    max-height: 100%;
    border: 0;
    border-radius: 0;
  }

  #ams-preview {
    right: 0;
  }

  // The comments panel pushes the page aside, as the editor's sidebar does (founder 19.10.2026).
  #ams-preview.with-comments {
    right: 380px;
  }
}

#pz-clients{
  color: #b7b7b7;
  background: #53575c url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' width='4' height='5' viewBox='0 0 4 5'%3e%3cpath fill='%23b7b7b7' d='M2 0L0 2h4zm0 5L0 3h4z'/%3e%3c/svg%3e") no-repeat right 0.75rem center/8px 10px;
  border: 0px solid #b7b7b7;
}

#preview-page {
  .modal-dialog {
    padding: 0;
    margin: 0;
    max-height: 100vh;
    height: 100vh;
    max-width: 100vw;

    .modal-header {
      background-color: #44484c;
      border-bottom: none;
      justify-content: flex-end;
      height: 65px;
      border-bottom: 1px solid #36393d;

      .active svg {
        color: $webmag-green !important;
      }
    }

    > .modal-content {
      height: 100%;

      .modal-body {
        padding: 0;
      }
    }
  }
}
</style>
